import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CandidateTimeline = ({ timeline }) => {
  const getTimelineIcon = (type) => {
    switch (type) {
      case 'application':
        return 'FileText';
      case 'screening':
        return 'Search';
      case 'interview':
        return 'Calendar';
      case 'status-change':
        return 'RefreshCw';
      case 'communication':
        return 'Mail';
      case 'note':
        return 'MessageSquare';
      default:
        return 'Circle';
    }
  };

  const getTimelineColor = (type) => {
    switch (type) {
      case 'application':
        return 'text-blue-600 bg-blue-100';
      case 'screening':
        return 'text-purple-600 bg-purple-100';
      case 'interview':
        return 'text-green-600 bg-green-100';
      case 'status-change':
        return 'text-amber-600 bg-amber-100';
      case 'communication':
        return 'text-indigo-600 bg-indigo-100';
      case 'note':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Clock" size={16} color="var(--color-primary)" />
        </div>
        <h3 className="font-semibold text-card-foreground">Candidate Journey</h3>
      </div>
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>

        {/* Timeline Items */}
        <div className="space-y-6">
          {timeline?.map((item, index) => (
            <div key={index} className="relative flex items-start space-x-4">
              {/* Timeline Icon */}
              <div className={`
                relative z-10 w-12 h-12 rounded-full flex items-center justify-center
                ${getTimelineColor(item?.type)}
              `}>
                <Icon name={getTimelineIcon(item?.type)} size={18} />
              </div>

              {/* Timeline Content */}
              <div className="flex-1 min-w-0 pb-6">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-card-foreground">{item?.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{item?.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-card-foreground">{item?.date}</p>
                    <p className="text-xs text-muted-foreground">{item?.time}</p>
                  </div>
                </div>

                {/* Additional Details */}
                {item?.details && (
                  <div className="mt-3 bg-muted/30 rounded-lg p-3">
                    <p className="text-sm text-muted-foreground">{item?.details}</p>
                  </div>
                )}

                {/* Responsible Person */}
                {item?.responsible && (
                  <div className="flex items-center space-x-2 mt-3">
                    <Image
                      src={item?.responsible?.avatar}
                      alt={item?.responsible?.name}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span className="text-sm text-muted-foreground">
                      by {item?.responsible?.name}
                    </span>
                    {item?.responsible?.role && (
                      <span className="text-xs text-muted-foreground">
                        • {item?.responsible?.role}
                      </span>
                    )}
                  </div>
                )}

                {/* Status Badge */}
                {item?.status && (
                  <div className="mt-3">
                    <span className={`
                      inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${item?.status === 'completed' ? 'status-excellent' :
                        item?.status === 'in-progress' ? 'status-good' :
                        item?.status === 'pending'? 'status-needs-review' : 'status-rejected'
                      }
                    `}>
                      <Icon 
                        name={
                          item?.status === 'completed' ? 'CheckCircle' :
                          item?.status === 'in-progress' ? 'Clock' :
                          item?.status === 'pending'? 'AlertCircle' : 'XCircle'
                        } 
                        size={12} 
                        className="mr-1" 
                      />
                      {item?.status?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </span>
                  </div>
                )}

                {/* Attachments or Links */}
                {item?.attachments && item?.attachments?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {item?.attachments?.map((attachment, attachIndex) => (
                      <div key={attachIndex} className="flex items-center space-x-2">
                        <Icon name="Paperclip" size={14} className="text-muted-foreground" />
                        <a 
                          href={attachment?.url} 
                          className="text-sm text-primary hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {attachment?.name}
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Future Timeline Item */}
        <div className="relative flex items-start space-x-4 opacity-50">
          <div className="relative z-10 w-12 h-12 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
            <Icon name="Plus" size={18} className="text-muted-foreground/50" />
          </div>
          <div className="flex-1 min-w-0 pb-6">
            <h4 className="font-medium text-muted-foreground">Next Steps</h4>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Upcoming activities will appear here
            </p>
          </div>
        </div>
      </div>
      {/* Timeline Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-primary">{timeline?.length}</div>
            <div className="text-xs text-muted-foreground">Total Activities</div>
          </div>
          <div>
            <div className="text-lg font-bold text-success">
              {timeline?.filter(item => item?.status === 'completed')?.length}
            </div>
            <div className="text-xs text-muted-foreground">Completed</div>
          </div>
          <div>
            <div className="text-lg font-bold text-accent">
              {Math.ceil((Date.now() - new Date(timeline[0]?.date)?.getTime()) / (1000 * 60 * 60 * 24))}
            </div>
            <div className="text-xs text-muted-foreground">Days Active</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateTimeline;